'use client';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useRouter } from 'next/navigation';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { STORE_VENDOR_ROUTE } from '@/utils/const';
import { addQueryParams } from '@/utils/helpers/addQueryParams';
import { getPageByPathname } from '@/utils/helpers/getPageByPathname';
import { useDebounce } from '@/utils/hooks/useDebounce';
import { fetchFilteredVendors } from '@/utils/hooks/useFilteredVendors';
import { IStore } from '@/utils/types/api';
import { VendorFilter } from '@/utils/types/filter';

export interface VendorsFiltersContextProps {
  setPage?: (value: number) => void;
  setLimit?: (value: number) => void;
  filters?: VendorFilter;
  isLoading?: boolean;
  vendorsData?:
    | {
        stores: IStore[];
        count: number;
      }
    | undefined;
  isError?: boolean;
  setPagePath?: React.Dispatch<React.SetStateAction<string>>;
  setSearchValue?: (value?: string) => void;
  debouncedFetch?: () => void;
}

export const VendorsFiltersContext = createContext<VendorsFiltersContextProps>(
  {},
);

interface VendorsFiltersProviderProps {
  children?: ReactNode;
}

export const VendorsFiltersProvider = (props: VendorsFiltersProviderProps) => {
  const { children } = props;
  const [filters, setFilters] = useState<VendorFilter>({} as VendorFilter);
  const [pagePath, setPagePath] = useState(''); // AKA: pathname
  const router = useRouter();

  const {
    data: vendorsData,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => fetchFilteredVendors(filters),
    refetchOnWindowFocus: false,
  });

  const debouncedFetch = useDebounce(() => {
    refetch();
  }, 500);

  useEffect(() => {
    const page = getPageByPathname(pagePath);

    if (!page || !pagePath) {
      return;
    }

    const params = addQueryParams(filters, pagePath);
    router.push(`${STORE_VENDOR_ROUTE}${params}`, { scroll: false });

    if (params.includes('q')) {
      router.push(`${STORE_VENDOR_ROUTE}${params}`, { scroll: false });
    }
  }, [filters]);

  const setLimit = useCallback((value: number) => {
    setFilters(prev => {
      const state = {
        ...prev,
        page: undefined,
        limit: value,
      };

      return state;
    });
  }, []);

  const setPage = useCallback((value: number) => {
    setFilters(prev => {
      const state = {
        ...prev,
        page: value,
      };

      return state;
    });
  }, []);

  const setSearchValue = useCallback((value?: string) => {
    setFilters(prev => {
      const state = {
        ...prev,
        page: undefined,
        q: value,
      };

      return state;
    });
  }, []);

  const defaultProps = useMemo(
    () => ({
      setPage,
      setLimit,
      filters,
      vendorsData,
      setPagePath,
      setSearchValue,
      debouncedFetch,
      isError,
      isLoading: isLoading,
    }),
    [
      setPage,
      setLimit,
      filters,
      vendorsData,
      setSearchValue,
      debouncedFetch,
      setPagePath,
      isError,
      isLoading,
    ],
  );

  return (
    <VendorsFiltersContext.Provider value={defaultProps}>
      {children}
    </VendorsFiltersContext.Provider>
  );
};
